// DTOs shared between the API (functions/) and the client (src/).
// Keep this file dependency-free.

export type ImageStatus = "pending" | "live" | "deleting";

export interface FolderDTO {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  path: string; // "products/phones", "" for root
  imageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ImageDTO {
  id: string;
  folderId: string | null;
  folderPath: string; // "" for root / uncategorized
  originalFilename: string;
  filename: string;
  extension: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  storagePath: string; // "/products/phones/iphone.webp"
  url: string; // absolute public URL
  status: ImageStatus;
  createdAt: string;
  updatedAt: string;
}

export interface SessionDTO {
  authenticated: boolean;
  username: string | null;
  mustChangePassword: boolean;
  csrfToken: string | null;
}

export interface DeploymentStatusDTO {
  driver: "local" | "cloudflare";
  state: "idle" | "queued" | "running" | "error";
  pendingImages: number;
  lastDeploymentAt: string | null;
  lastError: string | null;
  deploymentsThisMonth: number;
  monthlyDeploymentLimit: number;
}

export interface AppConfigDTO {
  driver: "local" | "cloudflare";
  publicAssetsBaseUrl: string;
  maxImageBytes: number;
  allowSvg: boolean;
  pagesFilesLimit: number;
  pagesFilesWarnAt: number;
  acceptMime: string[];
}

export interface StatsDTO {
  images: number;
  liveImages: number;
  pendingImages: number;
  folders: number;
  totalSizeBytes: number;
  pagesFilesUsed: number;
  pagesFilesLimit: number;
  pagesFilesWarnAt: number;
  assetCacheBytes: number;
  assetCacheLimitBytes: number;
}

export type ImageSort =
  | "newest"
  | "oldest"
  | "name-asc"
  | "name-desc"
  | "largest"
  | "smallest";

export interface ImageListQuery {
  folderId?: string | null; // undefined = all folders; null = uncategorised
  recursive?: boolean;
  q?: string;
  type?: string; // mime subtype filter, e.g. "png" | "jpeg" | "webp"
  sizeBucket?: "lt1" | "1to5" | "5to10" | "gt10";
  sort?: ImageSort;
  page?: number;
  pageSize?: number;
}

export interface ImageListResponse {
  images: ImageDTO[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ApiError {
  error: string;
  code?: string;
  fields?: Record<string, string>;
}

export const SUPPORTED_IMAGE_MIME = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/bmp",
  "image/tiff",
  "image/x-icon",
  "image/svg+xml",
] as const;

export const MIME_EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/avif": "avif",
  "image/bmp": "bmp",
  "image/tiff": "tiff",
  "image/x-icon": "ico",
  "image/svg+xml": "svg",
};
